import React from "react";
import { Button } from "@/components/ui/button";
import { Heart, MessageCircle, Repeat2, BarChart3, Bookmark, BookmarkCheck, Share, Link, Send } from "lucide-react";
import ViewsModal from "@/components/modals/views-modal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  trackPostLiked,
  trackPostShared,
  trackPostSaved,
  trackRepost,
  trackMediaViewed
} from "@/utils/analytics/content-events";

interface PostActionsProps {
  replyCount: number;
  likeCount: number;
  repostCount: number;
  bookmarkCount: number;
  viewCount: number;
  isLiked: boolean;
  isReposted: boolean;
  isBookmarked: boolean;
  onLike: (e: React.MouseEvent) => void;
  onRepost: () => void;
  onQuote: () => void;
  onBookmark: (e: React.MouseEvent) => void;
  onCopyLink?: () => void;
  onShare?: () => void;
  onSendDM?: () => void;
  isAnyModalOpen?: boolean;
  postId: string; // Add postId for analytics tracking
}

export function PostActions({
  replyCount,
  likeCount,
  repostCount,
  bookmarkCount,
  viewCount,
  isLiked,
  isReposted,
  isBookmarked,
  onLike,
  onRepost,
  onQuote,
  onBookmark,
  onCopyLink,
  onShare,
  onSendDM,
  isAnyModalOpen = false,
  postId,
}: PostActionsProps): JSX.Element {
  const [isRepostDropdownOpen, setIsRepostDropdownOpen] = React.useState(false);
  const [isShareDropdownOpen, setIsShareDropdownOpen] = React.useState(false);
  const [isViewsModalOpen, setIsViewsModalOpen] = React.useState(false);

  React.useEffect(() => {
    if (isAnyModalOpen) {
      setIsRepostDropdownOpen(false);
      setIsShareDropdownOpen(false);
    }
  }, [isAnyModalOpen]);

  return (
    <>
      <div className="flex justify-between w-full">
        <div className="flex items-center justify-between w-full">
          <Button variant="ghost" size="icon" className="relative z-20">
            <div className="flex items-center">
              <MessageCircle className="h-5 w-5" />
              <span className="ml-2 text-sm">{replyCount}</span>
            </div>
          </Button>

          <Button
            variant="ghost"
            size="icon"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation(); // Prevent post navigation
              
              // Track post like event
              trackPostLiked(postId, {
                source: 'feed',
                is_liked: !isLiked // Toggle state since this is before the state update
              });
              
              // Call the original handler
              onLike(e);
            }}
            className={`relative z-20 ${isLiked ? "text-red-500" : ""}`}
          >
            <div className="flex items-center">
              <Heart className={`h-5 w-5 ${isLiked ? "fill-current" : ""}`} />
              <span className="ml-2 text-sm">{likeCount}</span>
            </div>
          </Button>

          <Button
            variant="ghost"
            size="icon"
            className={`gap-2 ${isReposted || repostCount > 0 ? "text-green-500" : ""}`}
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              e.stopPropagation(); // Prevent post navigation
              e.stopPropagation();
            }}
          >
            <DropdownMenu
              open={isRepostDropdownOpen}
              onOpenChange={setIsRepostDropdownOpen}
            >
              <DropdownMenuTrigger asChild>
                <div className="flex items-center">
                  <Repeat2 className={`h-5 w-5 ${isReposted || repostCount > 0 ? "fill-current stroke-current" : ""}`} />
                  <span className="ml-2 text-sm">{repostCount}</span>
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => {
                  // Track repost event
                  trackRepost(postId, 'repost', {
                    source: 'feed',
                    is_reposted: !isReposted
                  });
                  
                  onRepost();
                  setIsRepostDropdownOpen(false);
                }}>
                  <Repeat2 className="mr-2 h-4 w-4" />
                  <span>{isReposted ? "Undo Repost" : "Repost"}</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => {
                  // Track quote post event
                  trackRepost(postId, 'quote', {
                    source: 'feed'
                  });
                  
                  onQuote();
                  setIsRepostDropdownOpen(false);
                }}>
                  <MessageCircle className="mr-2 h-4 w-4" />
                  <span>Quote Post</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </Button>

          <Button
            variant="ghost"
            size="icon"
            className="relative z-20"
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              e.stopPropagation(); // Prevent post navigation
              e.stopPropagation();
              setIsViewsModalOpen(true);
            }}
          >
            <div className="flex items-center">
              <BarChart3 className="h-5 w-5" />
              <span className="ml-2 text-sm">{viewCount}</span>
            </div>
          </Button>

          <Button
            variant="ghost"
            size="icon"
            className={`relative z-20 ${isBookmarked ? "text-blue-500" : ""}`}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation(); // Prevent post navigation
              
              // Track post bookmark event
              trackPostSaved(postId, {
                source: 'feed',
                is_bookmarked: !isBookmarked // Toggle state since this is before the state update
              });
              
              // Call the original handler
              onBookmark(e);
            }}
          >
            <div className="flex items-center">
              {isBookmarked ? (
                <BookmarkCheck className="h-5 w-5 fill-current" />
              ) : (
                <Bookmark className="h-5 w-5" />
              )}
              <span className="ml-2 text-sm">{bookmarkCount}</span>
            </div>
          </Button>

          <Button
            variant="ghost"
            size="icon"
            className="relative z-20"
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              e.stopPropagation(); // Prevent post navigation
              e.stopPropagation();
            }}
          >
            <DropdownMenu
              open={isShareDropdownOpen}
              onOpenChange={setIsShareDropdownOpen}
            >
              <DropdownMenuTrigger asChild>
                <div className="flex items-center">
                  <Share className="h-5 w-5" />
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => {
                  // Track share event - copy link
                  trackPostShared(postId, 'copy_link', {
                    source: 'feed'
                  });
                  
                  onCopyLink?.();
                  setIsShareDropdownOpen(false);
                }}>
                  <Link className="mr-2 h-4 w-4" />
                  <span>Copy Link</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => {
                  // Track share event - social
                  trackPostShared(postId, 'social', {
                    source: 'feed'
                  });
                  
                  onShare?.();
                  setIsShareDropdownOpen(false);
                }}>
                  <Share className="mr-2 h-4 w-4" />
                  <span>Share Post via</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => {
                  // Track share event - direct message
                  trackPostShared(postId, 'direct_message', {
                    source: 'feed'
                  });
                  
                  onSendDM?.();
                  setIsShareDropdownOpen(false);
                }}>
                  <Send className="mr-2 h-4 w-4" />
                  <span>Send via Direct Message</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </Button>
        </div>
      </div>
      <ViewsModal
        open={isViewsModalOpen}
        onOpenChange={setIsViewsModalOpen}
        viewCount={viewCount}
      />
    </>
  );
}